/**
 * 解析結果テーブル
 *
 * OCR解析結果の項目一覧表示
 */

import {
  Stack,
  Group,
  Text,
  Paper,
  Center,
  Box,
  ScrollArea,
  ActionIcon,
  Table,
  Alert,
  Badge,
  Loader,
  Divider,
  Accordion,
  Code,
} from "@mantine/core";
import { showInfo } from "../../utils/notifications";
import { Search, Copy, AlertCircle } from "lucide-react";
import { flattenObject } from "../../utils/jsonUtils";

// 値表示用ユーティリティ
function formatDisplayValue(val: unknown): string {
  if (val === undefined || val === null) return "";
  if (typeof val === "string") return val;
  if (typeof val === "number") return String(val);
  if (typeof val === "boolean") return val ? "はい" : "いいえ";
  if (typeof val === "object") return JSON.stringify(val);
  return String(val);
}

function isValueEmpty(val: unknown): boolean {
  if (val === undefined || val === null) return true;
  if (typeof val === "string" && val.trim() === "") return true;
  return false;
}

function DisplayValueText({
  value,
  size = "sm",
  fw,
}: {
  value: unknown;
  size?: string;
  fw?: number;
}) {
  const isEmpty = isValueEmpty(value);
  const displayValue = formatDisplayValue(value);

  return (
    <Text
      size={size}
      c={isEmpty ? "dimmed" : undefined}
      fw={fw}
      style={{ wordBreak: "break-all" }}
    >
      {isEmpty ? "(空)" : displayValue}
    </Text>
  );
}

interface AnalysisResultsTableProps {
  analyzing: boolean;
  analysisResult: Record<string, unknown> | null;
  errorMessage: string | null;
  promptUsed?: string | null;
}

export function AnalysisResultsTable({
  analyzing,
  analysisResult,
  errorMessage,
  promptUsed,
}: AnalysisResultsTableProps) {
  return (
    <Paper withBorder p="md" radius="md">
      <Stack gap="sm">
        <Group gap="xs" justify="space-between">
          <Group gap="xs">
            <Search size={16} />
            <Text size="sm" fw={700}>
              検出項目一覧
            </Text>
          </Group>
          {analysisResult && (
            <Badge variant="light" color="blue">
              {flattenObject(analysisResult).length} 項目
            </Badge>
          )}
        </Group>

        <Divider />

        <Box style={{ position: "relative", minHeight: "100px" }}>
          {analyzing ? (
            <Center py="xl">
              <Stack align="center" gap="xs">
                <Loader size="sm" variant="dots" />
                <Text size="xs" c="dimmed">
                  解析中...
                </Text>
              </Stack>
            </Center>
          ) : errorMessage ? (
            <Alert
              color="red"
              variant="light"
              title="解析失敗"
              icon={<AlertCircle size={16} />}
            >
              {errorMessage}
            </Alert>
          ) : analysisResult ? (
            <ScrollArea.Autosize mah="80vh" type="always">
              <Table striped highlightOnHover withTableBorder layout="fixed">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th w="40%">項目パス</Table.Th>
                    <Table.Th>値</Table.Th>
                    <Table.Th w={40} ta="center"></Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {flattenObject(analysisResult).map((p, i) => (
                    <Table.Tr key={i}>
                      <Table.Td>
                        <Text
                          size="xs"
                          fw={700}
                          style={{ wordBreak: "break-all" }}
                        >
                          {p.key}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <DisplayValueText value={p.value} size="xs" />
                      </Table.Td>
                      <Table.Td>
                        <Center>
                          <ActionIcon
                            variant="subtle"
                            color="gray"
                            size="sm"
                            onClick={() => {
                              navigator.clipboard.writeText(p.key);
                              showInfo(`「${p.key}」をコピーしました`);
                            }}
                          >
                            <Copy size={14} />
                          </ActionIcon>
                        </Center>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </ScrollArea.Autosize>
          ) : (
            <Center py="xl">
              <Text size="xs" c="dimmed">
                解析結果がここに表示されます
              </Text>
            </Center>
          )}
        </Box>

        {promptUsed && (
          <Accordion
            variant="subtle"
            chevronPosition="right"
            styles={{
              item: { border: "none" },
              control: { padding: "0" },
              content: { padding: "8px 0" },
            }}
          >
            <Accordion.Item value="prompt">
              <Accordion.Control>
                <Text size="xs" c="blue" style={{ cursor: "pointer" }}>
                  使用されたプロンプトを表示
                </Text>
              </Accordion.Control>
              <Accordion.Panel>
                <ScrollArea.Autosize mah={300}>
                  <Code block style={{ whiteSpace: "pre-wrap", fontSize: "11px" }}>
                    {promptUsed}
                  </Code>
                </ScrollArea.Autosize>
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>
        )}
      </Stack>
    </Paper>
  );
}
